import React from 'react';
import { Box, Tooltip, useTheme } from '@mui/material';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { latestNotesCommentBody, latestNotesCommentPreview } from '../utils/opportunityNotes';

type LastNotePreviewProps = {
  notes?: string | null;
};

const LastNotePreview: React.FC<LastNotePreviewProps> = ({ notes }) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const body = latestNotesCommentBody(notes || '');
  const preview = latestNotesCommentPreview(notes || '');
  const panelBorder = isDark ? '#2c3e50' : '#e0e0e0';
  const muted = isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.04)';

  if (!preview) return <>—</>;

  return (
    <Tooltip
      placement="bottom-start"
      enterDelay={350}
      enterNextDelay={200}
      slotProps={{
        tooltip: {
          sx: {
            bgcolor: isDark ? '#132436' : '#ffffff',
            color: theme.palette.text.primary,
            border: `1px solid ${panelBorder}`,
            boxShadow: 3,
            maxWidth: 480,
            p: 1.25,
          },
        },
      }}
      title={(
        <Box
          sx={{
            maxHeight: 320,
            overflow: 'auto',
            color: theme.palette.text.primary,
            '& h1, & h2, & h3, & h4, & h5, & h6': {
              fontWeight: 600,
              mt: 0.75,
              mb: 0.4,
              lineHeight: 1.3,
            },
            '& h1': { fontSize: '1.05rem' },
            '& h2': { fontSize: '0.98rem' },
            '& h3': { fontSize: '0.9rem' },
            '& p': { my: 0.4, lineHeight: 1.55, fontSize: '0.8125rem' },
            '& ul, & ol': {
              pl: 2.25,
              my: 0.4,
              '& li': { fontSize: '0.8125rem', lineHeight: 1.55, mb: 0.15 },
            },
            '& pre': {
              bgcolor: muted,
              border: `1px solid ${panelBorder}`,
              borderRadius: 1,
              p: 1,
              my: 0.75,
              overflowX: 'auto',
              '& code': { fontFamily: '"Roboto Mono", monospace', fontSize: '0.75rem', bgcolor: 'transparent', p: 0 },
            },
            '& :not(pre) > code': {
              bgcolor: muted,
              px: '5px',
              py: '1px',
              borderRadius: 0.5,
              fontFamily: '"Roboto Mono", monospace',
              fontSize: '0.82em',
            },
            '& blockquote': {
              borderLeft: `3px solid ${theme.palette.secondary.main}`,
              pl: 1.25,
              ml: 0,
              my: 0.75,
              color: theme.palette.text.secondary,
              fontStyle: 'italic',
            },
            '& strong': { fontWeight: 600 },
            '& a': { color: theme.palette.secondary.main },
          }}
        >
          <ReactMarkdown remarkPlugins={[remarkGfm]}>{body}</ReactMarkdown>
        </Box>
      )}
    >
      <Box
        component="span"
        sx={{
          display: 'block',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
          width: '100%',
          minWidth: 0,
        }}
      >
        {preview}
      </Box>
    </Tooltip>
  );
};

export default LastNotePreview;
